// Quais chamados cabem nas colunas de um Kanban — e quais não cabem.
//
// ## Por que "não cabe" precisa de nome
//
// O quadro só desenha o cartão cujo status (e substatus) casa alguma coluna do
// filtro. É a regra certa: sem ela, chamado de qualquer status era despejado na
// primeira coluna e um Kanban filtrado por finalizados vinha cheio de ativo.
//
// O efeito colateral aparece na BUSCA. A pessoa procura o chamado 267230
// dentro do Kanban Comercial, o servidor encontra, o chamado chega à tela — e
// some, porque o status dele não tem coluna ali. Não há erro, não há lista
// vazia: há um quadro com cartões, só que sem aquele. A leitura natural é "o
// sistema não acha o chamado" (Gisele, 27/10/2026).
//
// Daí este módulo: a mesma regra de sempre, mais a pergunta inversa, para a
// tela poder dizer quantos resultados ficaram de fora em vez de escondê-los.

/**
 * O status do chamado, venha ele de onde vier.
 *
 * Três formas na mesma listagem: `status_id` cru, a relação carregada, ou o
 * `status` numérico das telas antigas.
 */
const statusDoChamado = (chamado) =>
    chamado.status_id
    ?? chamado.ticket_status?.id
    ?? (chamado.status != null ? Number(chamado.status) : null);

const substatusDoChamado = (chamado) =>
    chamado.substatus_id ?? chamado.ticket_substatus?.id ?? null;

/**
 * O chamado pertence a esta coluna?
 *
 * @param {object} chamado
 * @param {{items: Array<{status_id: number, substatus_id: ?number}>}} coluna
 * @returns {boolean}
 */
export function casaColuna(chamado, coluna) {
    const status = statusDoChamado(chamado);
    const substatus = substatusDoChamado(chamado);
    const itens = Array.isArray(coluna?.items) ? coluna.items : [];

    return itens.some((it) => (
        it.status_id === status
        && (it.substatus_id == null ? substatus == null : it.substatus_id === substatus)
    ));
}

/**
 * Os chamados que nenhuma coluna do quadro aceita.
 *
 * Quadro SEM colunas devolve lista vazia, e não "todos de fora": é o estado de
 * quem ainda não escolheu um Kanban, e avisar que 300 chamados ficaram de fora
 * de um quadro que não existe seria ruído.
 *
 * @param {Array<object>} chamados
 * @param {Array<object>} colunas
 * @returns {Array<object>}
 */
export function foraDasColunas(chamados, colunas) {
    const lista = Array.isArray(chamados) ? chamados : [];
    const quadro = Array.isArray(colunas) ? colunas : [];

    if (quadro.length === 0) return [];

    return lista.filter((chamado) => !quadro.some((col) => casaColuna(chamado, col)));
}
